import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";
import { BadgeEtape, PointEtape } from "./badges";
import type { Etape, Pari, Portefeuille } from "./contract";
import {
  ageCourt,
  compter,
  estAncien,
  formatDate,
  joursDepuis,
  resteDuTitre,
  verdictEnClair,
} from "./format";
import { CeQuiTAttend } from "./portefeuille-attend";
import {
  type Demandes,
  nettoyerDemandes,
  RELECTURE_DEMANDE_MS,
  relectureDuPortefeuille,
  useDemandes,
  useTicTac,
} from "./portefeuille-demandes";
import { LienPari } from "./portefeuille-liens";
import {
  ETAPES_AU_REPOS,
  ETAPES_DU_FLUX,
  estAuRepos,
  plusAncien,
  SECTIONS,
} from "./portefeuille-tri";
import { usePortefeuille } from "./queries";
import { ConsoleShell } from "./shell";
import { Chargement, EcranDErreur, panneAffichee } from "./states";

/**
 * L'accueil de la console : ou en sont les paris, et ce qui attend Nicolas.
 *
 * Refonte de l'issue #125. La page tenait une table de huit colonnes triee par mise a jour :
 * lisible a 7 paris, illisible a 22, et sans hierarchie entre un pari qui vit et un pari
 * parque depuis une semaine. Elle est maintenant en trois temps : le pipeline dit d'un coup
 * d'oeil ou en est le portefeuille et sert de filtre, les paris vivants sont des cartes
 * groupees par etape, et ceux qui dorment attendent replies en bas.
 *
 * Ce que cette page ne fait toujours pas : **trier**. L'API rend les paris par mise a jour
 * decroissante et les gestes du plus ancien au plus recent ; la page ne fait que repartir
 * dans des sections en conservant cet ordre (`portefeuille-tri.ts`).
 */

/**
 * La source telle qu'elle s'affiche. « dossier » et non « idée » : « idée » est aussi une
 * etape, et une ligne « source idée, étape idée » ne disait plus rien. La cle du contrat,
 * elle, ne bouge pas.
 *
 * Le choix est unique la ou il etait multiple : a deux valeurs, cocher les deux revenait a
 * n'en cocher aucune, et deux pastilles pour trois etats se lisaient mal.
 */
const SOURCES = [
  { cle: "", libelle: "Tout" },
  { cle: "idee", libelle: "Dossiers" },
  { cle: "scout", libelle: "Scout" },
] as const;

export function PortefeuillePage({ maintenant = Date.now() }: { maintenant?: number }) {
  // Une demande « C'est fait » en attente : la liste se relit seule jusqu'a ce que son geste
  // la quitte, et la ligne du geste dit quand la demande est partie.
  const demandes = useDemandes(maintenant);
  const portefeuille = usePortefeuille((lu) => relectureDuPortefeuille(lu, demandes, Date.now()));
  const lu = portefeuille.data;
  // Et l'horloge tourne pendant ce temps : une relecture qui rend les memes donnees ne rend pas
  // la page, et une demande qui passe l'heure doit quand meme quitter sa ligne et le navigateur.
  useTicTac(RELECTURE_DEMANDE_MS, relectureDuPortefeuille(lu, demandes, maintenant) !== false);

  // Un geste qui a quitte la liste n'attend plus rien : il est fait, ou n'existe plus. Une
  // liste incomplete (un alimenteur en panne, l'avertissement le dit) ne prouve rien de tel,
  // et on n'y touche pas.
  useEffect(() => {
    if (lu === undefined || lu.avertissements.length > 0) return;
    nettoyerDemandes(
      Date.now(),
      lu.prerequis.flatMap((geste) => (geste.cible === null ? [] : [geste.cible])),
    );
  }, [lu]);

  return (
    <ConsoleShell connecte={portefeuille.isSuccess}>
      {portefeuille.isPending ? <Chargement quoi="du portefeuille" /> : null}
      {panneAffichee(portefeuille) ? (
        <EcranDErreur error={portefeuille.error} quoi="le portefeuille" />
      ) : null}
      {portefeuille.data && !panneAffichee(portefeuille) ? (
        <Contenu demandes={demandes} maintenant={maintenant} portefeuille={portefeuille.data} />
      ) : null}
    </ConsoleShell>
  );
}

function Contenu({
  portefeuille,
  maintenant,
  demandes,
}: {
  portefeuille: Portefeuille;
  maintenant: number;
  demandes: Demandes;
}) {
  const [etapes, setEtapes] = useState<readonly Etape[]>([]);
  const [source, setSource] = useState<string>("");
  // « Tout réafficher » disparait au moment ou on l'active : sans cible, le focus du
  // clavier retomberait sur `body`. Il passe a la phrase d'etat, juste au-dessus des cartes.
  const etat = useRef<HTMLParagraphElement>(null);

  const { paris, avertissements, genere_le } = portefeuille;
  const filtre = etapes.length > 0 || source !== "";
  const passe = (pari: Pari): boolean =>
    (etapes.length === 0 || etapes.includes(pari.etape)) &&
    (source === "" || pari.source === source);
  const visibles = paris.filter(passe);
  const vivants = paris.filter((pari) => !estAuRepos(pari.etape));
  const auRepos = paris.filter((pari) => estAuRepos(pari.etape));
  const auReposVisibles = auRepos.filter(passe);

  const basculerEtape = (etape: Etape): void => {
    setEtapes(
      etapes.includes(etape) ? etapes.filter((autre) => autre !== etape) : [...etapes, etape],
    );
  };

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <h1 className="text-2xl font-semibold">Portefeuille</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {compter(vivants.length, "pari vivant", "paris vivants")}, {auRepos.length} au repos.{" "}
            {genere_le
              ? `Index des idées généré le ${formatDate(genere_le)}.`
              : "Index des idées absent : âge des données inconnu."}
          </p>
        </div>
        <FiltreSource
          compte={(cle) => paris.filter((pari) => cle === "" || pari.source === cle).length}
          choisie={source}
          poser={setSource}
        />
      </header>

      <Pipeline basculer={basculerEtape} choisies={etapes} paris={paris} />

      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm text-muted-foreground">
        <p ref={etat} role="status" tabIndex={-1}>
          {filtre
            ? `${compter(visibles.length, "pari affiché", "paris affichés")} sur ${paris.length}.`
            : paris.length > 0
              ? "Les cartes vont du plus avancé au moins avancé."
              : ""}
        </p>
        {filtre ? (
          <button
            className="cursor-pointer rounded underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => {
              setEtapes([]);
              setSource("");
              etat.current?.focus();
            }}
            type="button"
          >
            Tout réafficher
          </button>
        ) : null}
      </div>

      {/* Les avertissements sont au-dessus des paris, jamais a leur place : une panne
          d'alimenteur ne doit pas masquer ce que l'autre a bien rendu. */}
      {avertissements.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {avertissements.map((avertissement) => (
            <li
              className="rounded-md border border-border bg-muted px-4 py-2 text-sm"
              key={avertissement}
              role="status"
            >
              {avertissement}
            </li>
          ))}
        </ul>
      ) : null}

      {/* Sur telephone, « ce qui t'attend » passe devant : c'est ce qui appelle une action,
          les cartes sont la pour s'y retrouver. Les deux colonnes n'arrivent qu'au-dela de
          80rem, ou le rail de 21rem ne mange plus la place des cartes.

          **Le rail est premier dans le DOM**, et `order` ne le deplace qu'a `xl`. L'inverse
          aurait fait lire le rail en premier a l'oeil et en dernier au clavier et au lecteur
          d'ecran, apres vingt-deux cartes (WCAG 1.3.2 et 2.4.3). A `xl` le desaccord restant
          est sans consequence : deux colonnes cote a cote se lisent dans les deux sens. */}
      <div className="grid grid-cols-1 items-start gap-x-8 gap-y-7 xl:grid-cols-[minmax(0,1fr)_21rem]">
        <div className="order-1 min-w-0 xl:order-2 xl:sticky xl:top-4">
          <CeQuiTAttend demandes={demandes} maintenant={maintenant} portefeuille={portefeuille} />
        </div>

        <div className="order-2 flex min-w-0 flex-col gap-7 xl:order-1">
          {SECTIONS.map((section) => {
            const dedans = visibles.filter((pari) => section.etapes.includes(pari.etape));
            if (dedans.length === 0) return null;
            return (
              <section aria-label={section.titre} className="flex flex-col gap-3" key={section.cle}>
                <h2 className="flex items-center gap-2 text-base font-semibold">
                  <PointEtape etape={section.point} />
                  {section.titre}
                  <span className="font-normal tabular-nums text-muted-foreground">
                    {dedans.length}
                  </span>
                  <span aria-hidden="true" className="h-px flex-1 bg-border" />
                </h2>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(min(19rem,100%),1fr))] gap-4">
                  {dedans.map((pari) => (
                    <Carte key={pari.id} maintenant={maintenant} pari={pari} />
                  ))}
                </div>
              </section>
            );
          })}

          {auReposVisibles.length > 0 ? <AuRepos paris={auReposVisibles} /> : null}

          {visibles.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {filtre ? "Aucun pari ne passe ces filtres." : "Aucun pari dans le portefeuille."}
            </p>
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * Le pipeline : une case par etape, avec son compte. Il dit ou en est le portefeuille, sert
 * de legende aux couleurs des badges et filtre les cartes, la ou la page avait une phrase
 * d'etat en haut et une rangee de pastilles au milieu.
 *
 * Les deux etapes de sortie sont detachees, en pointilles : elles ne sont pas la suite du
 * flux, elles en sont la porte de sortie.
 */
function Pipeline({
  paris,
  choisies,
  basculer,
}: {
  paris: readonly Pari[];
  choisies: readonly Etape[];
  basculer: (etape: Etape) => void;
}) {
  const compte = (etape: Etape): number => paris.filter((pari) => pari.etape === etape).length;
  const cases = (liste: readonly Etape[]) =>
    liste.map((etape) => (
      <CasePipeline
        choisi={choisies.includes(etape)}
        compte={compte(etape)}
        etape={etape}
        key={etape}
        onClick={() => basculer(etape)}
      />
    ));

  return (
    <fieldset className="flex min-w-0 flex-wrap gap-3">
      <legend className="sr-only">Filtrer par étape</legend>
      <div className="grid min-w-0 flex-[1_1_28rem] grid-cols-3 rounded-lg border border-input bg-card p-1 sm:grid-cols-6">
        {cases(ETAPES_DU_FLUX)}
      </div>
      {/* En pointilles, et detachees : les deux etapes de sortie ne sont pas la suite du
          flux, elles en sont la porte de sortie. Leur point est deja creux (`badges.tsx`).
          Meme contour que le cadre du flux, `border-input` : c'est celui d'un controle (3:1 au
          moins, WCAG 1.4.11). L'ancien `foreground/40` ne faisait que 2,5:1 en theme clair. */}
      <div className="grid min-w-0 flex-[1_1_10rem] grid-cols-2 rounded-lg border border-dashed border-input p-1">
        {cases(ETAPES_AU_REPOS)}
      </div>
    </fieldset>
  );
}

function CasePipeline({
  etape,
  compte,
  choisi,
  onClick,
}: {
  etape: Etape;
  compte: number;
  choisi: boolean;
  onClick: () => void;
}) {
  return (
    <button
      aria-label={`${etape}, ${compter(compte, "pari", "paris")}`}
      aria-pressed={choisi}
      className={cn(
        "flex cursor-pointer flex-col justify-center gap-0.5 rounded-md px-3 py-2 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:cursor-default disabled:opacity-45",
        // Un liseré intérieur plutôt qu'une bordure : la case ne bouge pas d'un pixel quand
        // on la choisit. Et pas d'inversion sur `primary`, qui bascule avec le thème.
        choisi ? "bg-muted shadow-[inset_0_0_0_2px_var(--foreground)]" : "enabled:hover:bg-muted",
      )}
      // Un filtre a zero ne peut que vider la liste. `!choisi` est une prudence : si la
      // lecture etait rafraichie un jour sous la page, un filtre pose puis tombe a zero doit
      // encore pouvoir s'oter.
      disabled={compte === 0 && !choisi}
      onClick={onClick}
      type="button"
    >
      <span className="text-xl font-semibold leading-tight tabular-nums">{compte}</span>
      <span className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
        <PointEtape etape={etape} />
        {etape}
      </span>
    </button>
  );
}

/** Les trois etats de la source, en un seul controle : tout, les dossiers, le scout. */
function FiltreSource({
  choisie,
  compte,
  poser,
}: {
  choisie: string;
  compte: (cle: string) => number;
  poser: (cle: string) => void;
}) {
  return (
    <fieldset className="flex rounded-full border border-input p-0.5 text-sm">
      <legend className="sr-only">Source</legend>
      {SOURCES.map((option) => (
        <label
          className={cn(
            "cursor-pointer rounded-full px-3 py-1 transition-colors",
            "has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
            choisie === option.cle
              ? "bg-foreground text-background"
              : "hover:bg-muted text-muted-foreground",
          )}
          key={option.cle || "tout"}
        >
          {/* Le compte est colle au libelle dans le texte visible : sans nom explicite, le
              controle s'appellerait « Scout1 ». */}
          <input
            aria-label={
              option.cle === ""
                ? option.libelle
                : `${option.libelle}, ${compter(compte(option.cle), "pari", "paris")}`
            }
            checked={choisie === option.cle}
            className="sr-only"
            name="source"
            onChange={() => poser(option.cle)}
            type="radio"
            value={option.cle}
          />
          {option.libelle}
          {option.cle === "" ? null : (
            <span className="ml-1.5 tabular-nums opacity-70">{compte(option.cle)}</span>
          )}
        </label>
      ))}
    </fieldset>
  );
}

/**
 * La carte d'un pari vivant. Le nom court fait le titre et le reste du libelle passe en
 * dessous : les titres de fiche font une phrase entiere, et en gras sur quatre lignes ils
 * faisaient un mur dans la table.
 */
function Carte({ pari, maintenant }: { pari: Pari; maintenant: number }) {
  const ouverts = pari.prerequis.filter((prerequis) => !prerequis.fait);
  const vieux = plusAncien(ouverts, maintenant);
  // La date du plus ancien, et non son age : `ageCourt` la remet en forme avec les memes
  // gardes que le rail (« sans date », « à venir »).
  const depuisLePlusAncien =
    ouverts.find((prerequis) => joursDepuis(prerequis.depuis, maintenant) === vieux)?.depuis ??
    null;
  const plusAncienEstAncien = estAncien(depuisLePlusAncien, maintenant);
  const description = resteDuTitre(pari.titre);
  const titreId = `pari-${pari.id.replace(/[^a-zA-Z0-9]/g, "-")}`;

  return (
    // Nommee par son titre : sans cela, vingt-deux regions anonymes dans l'arbre.
    <article
      aria-labelledby={titreId}
      className="flex min-w-0 flex-col gap-2.5 rounded-lg border border-border bg-card p-4 text-card-foreground"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <BadgeEtape etape={pari.etape} />
        {pari.piste === null ? null : (
          <span className="text-xs text-muted-foreground">{piste(pari)}</span>
        )}
        {pari.score === null ? null : (
          <span className="ml-auto text-right leading-tight">
            <span className="font-semibold tabular-nums">{pari.score}/10</span>
            <span className="block text-xs font-normal text-muted-foreground">
              {pari.plafond === null
                ? pari.source === "scout"
                  ? "grille du scout"
                  : ""
                : `plafond ${pari.plafond}`}
            </span>
          </span>
        )}
      </div>

      <h3 className="text-base font-semibold leading-snug" id={titreId}>
        <LienPari discret id={pari.id} titre={pari.titre} court />
      </h3>
      {description === "" ? null : (
        <p className="text-sm leading-snug text-muted-foreground">{description}</p>
      )}
      <p className="text-sm">
        {pari.verdict === null ? (
          <span className="text-muted-foreground">Pas encore de verdict</span>
        ) : (
          <Verdict verdict={pari.verdict} />
        )}
      </p>

      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border pt-2.5 text-xs text-muted-foreground">
        {/* Le meme seuil et le meme format que le rail : `estAncien` et `ageCourt`, jamais
            un 7 ecrit en dur ni un `${jours} j` qui rendrait « -3 j » sur une date a venir. */}
        <span className={cn(plusAncienEstAncien && "font-medium text-destructive")}>
          {ouverts.length === 0
            ? "Rien ne t'attend"
            : `${compter(ouverts.length, "geste", "gestes")}${
                vieux === null ? "" : ` · ${ageCourt(depuisLePlusAncien, maintenant)}`
              }`}
        </span>
        {pari.prochaineDate === null ? null : (
          <span className="whitespace-nowrap text-foreground">
            Lecture le {formatDate(pari.prochaineDate)}
          </span>
        )}
        <span className="ml-auto whitespace-nowrap tabular-nums">maj {formatDate(pari.maj)}</span>
      </div>
    </article>
  );
}

/** La piste, prefixee de sa source quand elle vient du scout : `A_etsy` seul ne dit rien. */
function piste(pari: Pari): string {
  return pari.source === "scout" ? `scout · ${pari.piste}` : `piste ${pari.piste}`;
}

/**
 * Le verdict en tete, et l'ecart a la recommandation en etiquette. Les fiches ecrivent
 * « GO build, décision de Nicolas le 18 septembre 2026 contre la recommandation PARQUER : ... » :
 * la phrase entiere tient sur la fiche, la carte en garde ce qui se lit d'un regard.
 */
function Verdict({ verdict }: { verdict: string }) {
  const clair = verdictEnClair(verdict);
  const tete = clair.split(/[,:]/)[0]?.trim() || clair;
  // La recommandation ecartee tient en un mot (PARQUER, GO), le reste de la phrase est sur
  // la fiche. Sans correspondance, l'etiquette ne s'affiche pas.
  const reco = /contre la recommandation\s+([A-Za-zÀ-ÿ]+)/.exec(clair)?.[1];
  return (
    <>
      <span className="font-semibold" title={clair}>
        {tete}
      </span>
      {reco === undefined ? null : (
        <span className="ml-2 whitespace-nowrap rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
          contre la reco {reco}
        </span>
      )}
    </>
  );
}

/**
 * Les paris qui dorment, replies. C'est la part du portefeuille qui grossit le plus vite
 * (neuf idees parquees en une semaine) et celle qui ne demande rien : une ligne dense par
 * pari suffit, avec la raison qui a ferme le dossier.
 */
function AuRepos({ paris }: { paris: readonly Pari[] }) {
  const derniere = paris.reduce((recente, pari) => (pari.maj > recente ? pari.maj : recente), "");
  const compteEtape = (etape: Etape): number => paris.filter((pari) => pari.etape === etape).length;

  return (
    // `aria-label` sur le `details` : son sommaire ne le nomme pas tout seul partout.
    <details aria-label="Au repos" className="group rounded-lg border border-border">
      <summary className="flex cursor-pointer flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg p-3 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <span className="flex items-baseline gap-1.5 font-semibold">
          <span
            aria-hidden="true"
            className="text-muted-foreground transition-transform group-open:rotate-90"
          >
            &rsaquo;
          </span>
          Au repos
          <span className="font-normal tabular-nums text-muted-foreground">{paris.length}</span>
        </span>
        <span className="text-sm text-muted-foreground">
          {compter(compteEtape("parquée"), "parquée", "parquées")},{" "}
          {compter(compteEtape("tuée"), "tuée", "tuées")}
          {derniere === "" ? "" : ` · la dernière le ${formatDate(derniere)}`}
        </span>
      </summary>
      <ul aria-label="Paris au repos" className="flex flex-col px-3 pb-1">
        {paris.map((pari) => (
          <li
            className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3 gap-y-0.5 border-t border-border py-2.5 text-sm sm:grid-cols-[5.5rem_minmax(9rem,16rem)_3rem_minmax(0,1fr)_3.5rem]"
            key={pari.id}
          >
            <span className="row-span-2 sm:row-span-1">
              <BadgeEtape etape={pari.etape} />
            </span>
            <span className="min-w-0 truncate font-medium">
              <LienPari court discret id={pari.id} titre={pari.titre} />
            </span>
            <span className="tabular-nums text-muted-foreground sm:text-right">
              {pari.score === null ? "" : `${pari.score}/10`}
            </span>
            {/* Une ligne, pas deux : la raison entiere est sur la fiche, et au survol. */}
            <span
              className="col-span-2 min-w-0 truncate text-muted-foreground sm:col-span-1"
              title={pari.pourquoi ?? verdictEnClair(pari.verdict)}
            >
              {pari.pourquoi === null
                ? verdictEnClair(pari.verdict)
                : premierePhrase(pari.pourquoi)}
            </span>
            <span className="col-span-2 whitespace-nowrap tabular-nums text-muted-foreground sm:col-span-1 sm:text-right">
              {formatDate(pari.maj)}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * La premiere phrase d'un « pourquoi », qui en fait plusieurs : de quoi dire ce qui a ferme
 * le dossier sans deplier le paragraphe. Le texte entier reste sur la fiche.
 */
function premierePhrase(pourquoi: string): string {
  const fin = pourquoi.search(/\.\s|\. ?$/);
  return fin < 0 ? pourquoi : pourquoi.slice(0, fin);
}
